import { useEffect, useState, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  CalendarDays, MapPin, Users, DollarSign, Mail, Phone, Loader2,
  Send, XCircle, CheckCircle2, Clock, Ban, Sparkles, FileText,
} from "lucide-react";

/**
 * VendorBookings — vendor's private-booking inbox.
 *
 * Tabs: Pending (requested) · Quoted · Paid · Past
 * Each row shows all details from the booking request; primary actions are
 *   - Send quote (Dialog with quote_cents + optional message)
 *   - Decline (Dialog with optional reason)
 *
 * The refund ladder for paid bookings is surfaced on the row as a soft hint
 * ("If cancelled today the customer would receive X back") so the vendor
 * always knows where they stand.
 */
export default function VendorBookings() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/private-bookings/mine");
      setItems(data.items || []);
    } catch (e) {
      toast.error("Couldn't load bookings");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user?.role !== "vendor") return;
    load();
  }, [user, load]);

  const groups = useMemo(() => ({
    pending: items.filter((b) => b.status === "requested"),
    quoted: items.filter((b) => b.status === "quoted"),
    paid: items.filter((b) => b.status === "paid"),
    past: items.filter((b) => ["completed", "cancelled", "declined"].includes(b.status)),
  }), [items]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login?next=/vendor/bookings" replace />;
  if (user.role !== "vendor") return <Navigate to="/" replace />;

  const tabs = [
    { key: "pending", label: "Pending", count: groups.pending.length, icon: Clock },
    { key: "quoted", label: "Quoted", count: groups.quoted.length, icon: Send },
    { key: "paid", label: "Confirmed", count: groups.paid.length, icon: CheckCircle2 },
    { key: "past", label: "Past", count: groups.past.length, icon: Sparkles },
  ];

  return (
    <div className="mx-auto max-w-5xl px-6 py-10" data-testid="vendor-bookings-page">
      <div>
        <div className="overline" style={{ color: "var(--avt-purple)" }}>Private bookings</div>
        <h1 className="font-serif text-4xl mt-3">Your inbox.</h1>
        <p className="mt-1 text-sm max-w-2xl" style={{ color: "var(--ink-soft)" }}>
          When someone scans your QR or opens your profile they can request a
          private booking. Reply with a quote — you get paid through Stripe
          the moment they accept.
        </p>
      </div>

      <div className="mt-6 flex items-center gap-2 border-b overflow-x-auto"
           style={{ borderColor: "var(--border-warm)" }} data-testid="bookings-tabs">
        {tabs.map(({ key, label, count, icon: Icon }) => (
          <button
            key={key}
            data-testid={`bookings-tab-${key}`}
            onClick={() => setTab(key)}
            className="px-4 py-2 text-sm font-semibold transition-colors inline-flex items-center gap-2"
            style={{
              color: tab === key ? "var(--avt-purple)" : "var(--ink-soft)",
              borderBottom: tab === key ? "2px solid var(--avt-purple)" : "2px solid transparent",
            }}
          >
            <Icon className="h-3.5 w-3.5" /> {label}
            <span className="text-[10px] px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: "var(--sand)", color: "var(--ink-soft)" }}>
              {count}
            </span>
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        {loading ? (
          <div className="text-center py-16" data-testid="bookings-loading">
            <Loader2 className="h-8 w-8 mx-auto animate-spin" style={{ color: "var(--avt-purple)" }} />
          </div>
        ) : groups[tab].length === 0 ? (
          <EmptyState tab={tab} />
        ) : (
          groups[tab].map((b) => (
            <BookingRow key={b.id} b={b} onChanged={load} />
          ))
        )}
      </div>
    </div>
  );
}

function EmptyState({ tab }) {
  const copy = {
    pending: "No new requests right now. Print your QR code and stick it on your booth — customers can request bookings on the spot.",
    quoted: "No open quotes. When you send a quote, it lives here until the customer pays or the booking is cancelled.",
    paid: "No confirmed bookings yet. When a customer pays your quote, the booking lands here.",
    past: "No past bookings to review.",
  };
  return (
    <div className="card-warm rounded-2xl p-8 text-center" data-testid="bookings-empty">
      <p className="text-sm italic" style={{ color: "var(--ink-soft)" }}>{copy[tab]}</p>
    </div>
  );
}

function BookingRow({ b, onChanged }) {
  const badge = STATUS_BADGE[b.status] || STATUS_BADGE.default;
  return (
    <div className="card-warm rounded-2xl p-5"
         data-testid={`booking-row-${b.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif text-xl">{b.event_type}</h3>
            <span
              className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: badge.bg, color: badge.fg }}
              data-testid={`booking-status-${b.id}`}
            >
              {badge.label}
            </span>
          </div>
          <div className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}>
            {b.customer_name} · <a href={`mailto:${b.customer_email}`} className="underline">{b.customer_email}</a>
            {b.customer_phone && <> · <a href={`tel:${b.customer_phone}`} className="underline">{b.customer_phone}</a></>}
          </div>
        </div>
        <div className="text-right">
          <div className="font-serif text-2xl" style={{ color: "var(--avt-purple)" }}>
            {b.status === "quoted" || b.status === "paid" || b.status === "cancelled"
              ? `$${((b.quote_cents || 0) / 100).toLocaleString()}`
              : `$${((b.proposed_budget_cents || 0) / 100).toLocaleString()}`}
          </div>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
            {b.status === "requested" ? "customer budget" : "quote"}
          </div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
        <Meta icon={CalendarDays}>{b.event_date}{b.event_time ? ` · ${b.event_time}` : ""}</Meta>
        <Meta icon={MapPin}>{b.event_location}</Meta>
        {b.guest_count && <Meta icon={Users}>{b.guest_count} guests</Meta>}
      </div>

      {b.wishlist && (
        <div className="mt-3 rounded-md p-3" style={{ backgroundColor: "var(--sand)" }}>
          <div className="text-[10px] uppercase tracking-widest mb-1"
               style={{ color: "var(--ink-soft)" }}>What they'd like</div>
          <div className="text-sm whitespace-pre-wrap">{b.wishlist}</div>
        </div>
      )}

      {b.quote_message && (
        <div className="mt-3 rounded-md p-3" style={{ backgroundColor: "rgba(107,47,186,0.06)" }}>
          <div className="text-[10px] uppercase tracking-widest mb-1"
               style={{ color: "var(--avt-purple)" }}>Your note to the customer</div>
          <div className="text-sm whitespace-pre-wrap">{b.quote_message}</div>
        </div>
      )}

      {b.status === "paid" && typeof b.refund_bps_if_cancelled_today === "number" && (
        <div className="mt-3 text-xs" style={{ color: "var(--ink-soft)" }}>
          If cancelled today, refund would be
          {" "}<strong>${(((b.quote_cents || 0) * b.refund_bps_if_cancelled_today / 10000) / 100).toFixed(2)}</strong>
          {" "}(policy: 100% ≥14d, 50% 6–13d, 0% ≤5d).
        </div>
      )}

      {(b.status === "requested" || b.status === "quoted") && (
        <div className="mt-4 flex flex-wrap gap-2">
          <SendQuoteDialog b={b} onDone={onChanged} />
          <DeclineDialog b={b} onDone={onChanged} />
        </div>
      )}

      {(b.status === "quoted" || b.status === "paid") && (
        <div className="mt-3 flex flex-wrap gap-2">
          <InvoiceFromBookingButton b={b} />
        </div>
      )}
    </div>
  );
}

function InvoiceFromBookingButton({ b }) {
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/documents/invoices/from-booking", { booking_id: b.id });
      toast.success(`Draft invoice ${data.number} created. Opening…`);
      setTimeout(() => { window.location.href = "/vendor/documents"; }, 400);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Invoicing is a Maker Pro+ feature.", {
          action: { label: "See Pro+", onClick: () => { window.location.href = "/pricing"; } },
        });
      } else {
        const d = e.response?.data?.detail;
        toast.error(typeof d === "object" ? d.message : d || "Could not create invoice");
      }
    } finally { setBusy(false); }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      className="rounded-full inline-flex items-center gap-1.5"
      data-testid={`invoice-from-booking-${b.id}`}
      onClick={create}
      disabled={busy}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
      Create invoice
    </Button>
  );
}

const STATUS_BADGE = {
  requested: { label: "New request", bg: "rgba(249,115,22,0.15)", fg: "#c2410c" },
  quoted:    { label: "Quote sent",  bg: "rgba(44,196,217,0.20)", fg: "#0e7490" },
  paid:      { label: "Confirmed",   bg: "rgba(31,93,52,0.20)",  fg: "#1f5d34" },
  cancelled: { label: "Cancelled",   bg: "rgba(0,0,0,0.08)",     fg: "#5b524a" },
  declined:  { label: "Declined",    bg: "rgba(0,0,0,0.08)",     fg: "#5b524a" },
  completed: { label: "Completed",   bg: "rgba(107,47,186,0.15)",fg: "var(--avt-purple)" },
  default:   { label: "—",           bg: "var(--sand)",           fg: "var(--ink-soft)" },
};

function Meta({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2" style={{ color: "var(--ink)" }}>
      <Icon className="h-3.5 w-3.5" style={{ color: "var(--avt-purple)" }} />
      <span>{children}</span>
    </div>
  );
}

function SendQuoteDialog({ b, onDone }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(((b.quote_cents ?? b.proposed_budget_cents) / 100) || "");
  const [message, setMessage] = useState(b.quote_message || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy) return;
    const cents = Math.round(parseFloat(amount || 0) * 100);
    if (!(cents >= 100)) { toast.error("Enter a valid quote amount"); return; }
    setBusy(true);
    try {
      await api.post(`/private-bookings/${b.id}/quote`,
        { quote_cents: cents, quote_message: message.trim() || null });
      toast.success("Quote sent — the customer will get an email with a pay link.");
      setOpen(false);
      onDone?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Couldn't send quote";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="btn-primary rounded-full"
                data-testid={`booking-send-quote-${b.id}`}>
          <Send className="h-3.5 w-3.5 mr-1.5" />
          {b.status === "quoted" ? "Update quote" : "Send quote"}
        </Button>
      </DialogTrigger>
      <DialogContent style={{ backgroundColor: "var(--paper-warm)" }}
                     data-testid="send-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Quote {b.customer_name}</DialogTitle>
          <DialogDescription>
            Send a firm price. The customer gets an email with a secure pay link.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest"
                   style={{ color: "var(--ink-soft)" }}>Your quote (USD)</label>
            <Input type="number" min="1" step="1" value={amount}
                   onChange={(e) => setAmount(e.target.value)}
                   data-testid="send-quote-amount" placeholder="500" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest"
                   style={{ color: "var(--ink-soft)" }}>Note to the customer (optional)</label>
            <Textarea rows={4} value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      data-testid="send-quote-message"
                      placeholder="Includes setup, service for 30 guests, cleanup. Additional guests $12 each." />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setOpen(false)}
                  className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  className="btn-primary rounded-full"
                  data-testid="send-quote-submit">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                   : <><Send className="h-4 w-4 mr-2" /> Send quote</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeclineDialog({ b, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/private-bookings/${b.id}/decline`,
        { reason: reason.trim() || null });
      toast.success("Booking declined — the customer has been notified.");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error("Couldn't decline booking");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline"
                className="rounded-full"
                data-testid={`booking-decline-${b.id}`}>
          <Ban className="h-3.5 w-3.5 mr-1.5" /> Decline
        </Button>
      </DialogTrigger>
      <DialogContent style={{ backgroundColor: "var(--paper-warm)" }}
                     data-testid="decline-dialog">
        <DialogHeader>
          <DialogTitle>Decline this booking?</DialogTitle>
          <DialogDescription>
            The customer will be emailed. A short reason is a kindness — but
            entirely optional.
          </DialogDescription>
        </DialogHeader>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Booked that weekend already, but I have Saturdays open in October!"
                  data-testid="decline-reason" />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setOpen(false)}
                  className="rounded-full">Nevermind</Button>
          <Button onClick={submit} disabled={busy}
                  className="rounded-full"
                  data-testid="decline-submit"
                  style={{ backgroundColor: "#b91c1c", color: "#fff" }}>
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                   : <><XCircle className="h-4 w-4 mr-2" /> Decline booking</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
